import {
  computeQ4TimeShareDomesticBreakdownRows,
  sumDomesticBreakdownRows,
  type Q4DomesticBreakdownRow,
} from "./dunleavyQ4ComparisonDomesticBreakdownCore";
import { money } from "./money";
import type { FlightLeg, Rates } from "./types";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Comparison tab: row-by-row domestic / intl detail for the given legs (usually Q4 2025 `[Time share]`). */
export function dunleavyQ4LogbookDomesticBreakdownPanelHtml(
  legs: FlightLeg[],
  worksheetRates: Rates,
  worksheetDefaultDomesticSegmentUsd: number
): string {
  const domWs = worksheetRates.domesticSegmentUsdPerPassengerPerSegment;
  let rows: Q4DomesticBreakdownRow[];
  try {
    rows = computeQ4TimeShareDomesticBreakdownRows(
      legs,
      worksheetRates,
      worksheetDefaultDomesticSegmentUsd
    );
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return `
    <section class="panel panel-tight">
      <h3 class="agreements-pane-title">Worksheet — IRS No. 26 / 27 (detail)</h3>
      <p class="error" role="alert">Could not build breakdown: ${escapeHtml(msg)}</p>
    </section>`;
  }

  if (rows.length === 0) {
    return `
    <section class="panel panel-tight">
      <h3 class="agreements-pane-title">Worksheet — IRS No. 26 / 27 (detail)</h3>
      <p class="sheets-hint">No Q4 2025 <code>[Time share]</code> legs in the current worksheet.</p>
    </section>`;
  }

  const { units, domesticTax, domesticTaxCy2025 } = sumDomesticBreakdownRows(rows);
  const altDomesticUsd = worksheetDefaultDomesticSegmentUsd;
  const ifRate = worksheetRates.internationalFacilityUsdPerPerson;
  let intlPaxSum = 0;
  let intlTax = 0;
  for (const row of rows) {
    intlPaxSum += row.intlPax;
    intlTax = money(intlTax + row.intlPax * ifRate);
  }

  const body = rows
    .map(
      (row) => `<tr>
    <td>${escapeHtml(row.flightDate)}</td>
    <td><code>${escapeHtml(row.route)}</code></td>
    <td class="comparison-num">${row.csvPax}</td>
    <td class="comparison-num">${escapeHtml(row.domPaxDisplay)}</td>
    <td class="comparison-num">${row.segments}</td>
    <td>${row.rural ? "yes" : ""}</td>
    <td class="comparison-num">${row.effPax}</td>
    <td class="comparison-num">${row.segmentUnits}</td>
    <td class="comparison-num">${row.domesticTax.toFixed(2)}</td>
    <td class="comparison-num">${row.domesticTaxCy2025.toFixed(2)}</td>
    <td class="comparison-num">${row.intlPax}</td>
    <td class="muted-small comparison-note">${escapeHtml(row.summary)}</td>
  </tr>`
    )
    .join("");

  return `
    <section class="panel">
      <h3 class="agreements-pane-title">Worksheet — IRS No. 26 (row-by-row) and No. 27 totals</h3>
      <p class="sheets-hint muted-small">
        Same rules as <strong>Form 720</strong>: for each leg, <strong>domestic segment tax</strong> =
        <code>segments × effective domestic passengers × rate</code> when <code>rural</code> is off and effective pax &gt; 0.
        The first <strong>Dom. $</strong> column uses your <strong>Form 720</strong> domestic rate
        <strong>$${domWs.toFixed(2)}</strong>/segment-unit; the next column uses the
        <strong>Comparison alternate</strong> domestic rate (same as worksheet default <strong>$${altDomesticUsd.toFixed(2)}</strong> here) — same segment-units.
        <strong>Effective passengers</strong> for IRS No. 26: <code>dom_pax</code> when set; otherwise row <code>pax</code>
        (<code>passenger_count</code>). On <strong>foreign–foreign</strong> legs (neither endpoint K/PA/PH domestic US)
        effective domestic pax is <strong>0</strong> unless <code>dom_pax</code> is set (<code>passenger_count</code> ignored there).
        <strong>IRS No. 27</strong> international facility tax is separate
        (<code>effective passengers × $${ifRate.toFixed(2)}</code> from Form 720).
        That column is the stored <code>intl_pax</code> headcount only when the route is <strong>US ↔ non-US</strong>;
        US–US and foreign–foreign use <strong>0</strong> here (same rules as Form 720).
      </p>
      <p class="sheets-hint muted-small">
        <strong>Totals for these ${rows.length} legs:</strong> <strong>${units}</strong> segment-units →
        <strong>$${domesticTax.toFixed(2)}</strong> domestic @ $${domWs.toFixed(2)} /
        <strong>$${domesticTaxCy2025.toFixed(2)}</strong> domestic @ $${altDomesticUsd.toFixed(2)};
        <strong>${intlPaxSum}</strong> effective passengers (No. 27) →
        <strong>$${intlTax.toFixed(2)}</strong> international facility (should match the <strong>Form 720 rates</strong> column in <strong>Totals</strong> above).
      </p>
      <div class="comparison-table-wrap">
        <table class="totals comparison-table comparison-breakdown-table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Route</th>
              <th scope="col">CSV <code>pax</code></th>
              <th scope="col"><code>dom_pax</code></th>
              <th scope="col"><code>segments</code></th>
              <th scope="col">Rural</th>
              <th scope="col" title="Effective passengers for IRS No. 26 domestic segment">Eff. pax</th>
              <th scope="col">Units</th>
              <th scope="col">Dom. $ @ $${domWs.toFixed(2)}</th>
              <th scope="col">Dom. $ @ $${altDomesticUsd.toFixed(2)}</th>
              <th scope="col" title="Effective passengers for IRS No. 27 (intl_pax when US ↔ non-US only)">Effective passengers</th>
              <th scope="col">How counted</th>
            </tr>
          </thead>
          <tbody>
            ${body}
            <tr class="comparison-match">
              <th scope="row" colspan="7">Total (segment-units / domestic segment tax)</th>
              <td class="comparison-num"><strong>${units}</strong></td>
              <td class="comparison-num"><strong>${domesticTax.toFixed(2)}</strong></td>
              <td class="comparison-num"><strong>${domesticTaxCy2025.toFixed(2)}</strong></td>
              <td></td>
              <td></td>
            </tr>
            <tr class="comparison-match">
              <th scope="row" colspan="10">Total (IRS No. 27 — effective passengers × $${ifRate.toFixed(2)})</th>
              <td class="comparison-num"><strong>${intlPaxSum}</strong></td>
              <td class="comparison-num"><strong>$${intlTax.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>`;
}
